import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface ExpensePartyRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export type ExpensePartyDraft = {
  name: string;
};

const LS_KEY = "inventory.expenseParties.v1";

type ExpensePartyApiRecord = {
  id?: number | string;
  name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertExpensePartyApiResponse = {
  message?: string;
  data?: ExpensePartyApiRecord;
};

type UpsertExpensePartyResult = {
  record: ExpensePartyRecord;
  message: string;
};

function dedupeById(items: ExpensePartyRecord[]): ExpensePartyRecord[] {
  const byId = new Map<ID, ExpensePartyRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): ExpensePartyRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ExpensePartyRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: ExpensePartyRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): ExpensePartyApiRecord[] {
  if (Array.isArray(body)) return body as ExpensePartyApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as ExpensePartyApiRecord[];
  return [];
}

function extractPartyFromUpsertBody(body: unknown): ExpensePartyApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const data = (body as Record<string, unknown>).data;
  if (data && typeof data === "object" && !Array.isArray(data)) return data as ExpensePartyApiRecord;
  return null;
}

function mapApiRecord(record: ExpensePartyApiRecord): ExpensePartyRecord {
  const created = record.created_at || new Date().toISOString();
  return {
    id: String(record.id ?? ""),
    name: (record.name ?? "").toString(),
    createdAt: created,
    updatedAt: record.updated_at || created,
  };
}

export function listExpenseParties(): ExpensePartyRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

export async function fetchExpenseParties(): Promise<ExpensePartyRecord[]> {
  const response = await api.get("/api/v1/expense-parties");
  const records = dedupeById(
    extractRowsFromResponse(response.data)
      .map(mapApiRecord)
      .filter((r) => Boolean(r.id)),
  );
  save(records);
  return listExpenseParties();
}

export async function createExpenseParty(draft: ExpensePartyDraft): Promise<UpsertExpensePartyResult> {
  const items = load();
  const response = await api.post<UpsertExpensePartyApiResponse>("/api/v1/expense-parties", {
    name: draft.name.trim(),
  });
  const created = extractPartyFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create expense party response.");

  const record = mapApiRecord(created);
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Expense party created successfully." };
}
